import { TRACK_PERFORMANCE } from '@/defaults'
import { convertNanoToMilliSeconds } from './convertSeconds'
import { sum } from './sum'

export function createTimestampQuery<T extends string>(
  device: GPUDevice,
  timestampNames: T[],
  avgCount = 5,
  /**
   * Each timestamp pair can be written into N different locations,
   * changing location each frame. This seems to help with preventing
   * a race condition while reading the timestamps.
   */
  pairLocationCount = 32,
) {
  if (!TRACK_PERFORMANCE) {
    const timings = Object.fromEntries(
      timestampNames.map((name) => [name, 1]), // dummy start at 1ms
    ) as Record<T, number>
    const writeTimes: { time: number; div: number }[] = []

    return {
      timestampWrites: () =>
        Object.fromEntries(
          timestampNames.map((name) => [name, undefined]),
        ) as Record<T, undefined>,
      write: (encoder: GPUCommandEncoder, div = 1) => {
        writeTimes.push({ time: globalThis.performance.now(), div })
      },
      read: () => {
        const item = writeTimes.shift()
        if (item !== undefined) {
          const durationMs = globalThis.performance.now() - item.time
          // Assign total time divided by iterations to the first timestamp (ifsMs)
          // Without GPU timestamps, we measure CPU-to-GPU wall latency which includes constant
          // browser/scheduling overhead. Enforce a minimum floor on the calculated per-iteration
          // time to prevent the estimator from escalating iteration count to 1000 under scheduling latency.
          const calculatedIfsMs = durationMs / Math.max(item.div, 1)
          timings[timestampNames[0] as T] = Math.max(calculatedIfsMs, 0.25)
        }
      },
      average: () => timings,
    }
  }
  const timestampCount = timestampNames.length

  const timestampQuerySet = device.createQuerySet({
    type: 'timestamp',
    count: timestampCount * 2 * pairLocationCount * 2,
  })

  const timestampBuffer = device.createBuffer({
    size: timestampQuerySet.count * BigInt64Array.BYTES_PER_ELEMENT,
    usage: GPUBufferUsage.QUERY_RESOLVE | GPUBufferUsage.COPY_SRC,
  })

  const timestampMappable = device.createBuffer({
    size: timestampBuffer.size,
    usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
  })

  // Timestamp buffers are intentionally NOT destroyed — they may still be
  // referenced by in-flight GPU work when the component unmounts. Letting the
  // GC reclaim them avoids "Buffer used while destroyed" errors.

  function timestampWrites(frameId: number) {
    const locationIndex = (frameId % pairLocationCount) * timestampCount * 2
    return Object.fromEntries(
      timestampNames.map((name, i) => [
        name,
        {
          querySet: timestampQuerySet,
          beginningOfPassWriteIndex: locationIndex + i * 2,
          endOfPassWriteIndex: locationIndex + i * 2 + 1,
        },
      ]),
    ) as Record<T, GPUComputePassTimestampWrites>
  }

  function write(encoder: GPUCommandEncoder, _div = 1) {
    encoder.resolveQuerySet(
      timestampQuerySet,
      0,
      timestampQuerySet.count,
      timestampBuffer,
      0,
    )

    if (timestampMappable.mapState === 'unmapped') {
      encoder.copyBufferToBuffer(
        timestampBuffer,
        0,
        timestampMappable,
        0,
        timestampMappable.size,
      )
    }
  }

  const latest: Record<T, number>[] = []

  async function read(frameId: number) {
    if (timestampMappable.mapState !== 'unmapped') {
      return
    }
    const locationIndex = (frameId % pairLocationCount) * timestampCount * 2
    await timestampMappable.mapAsync(GPUMapMode.READ)
    const times = new BigInt64Array(timestampMappable.getMappedRange())
    const results = Object.fromEntries(
      timestampNames.map((name, i) => {
        const begin = times[locationIndex + i * 2]!
        const end = times[locationIndex + i * 2 + 1]!
        // BigInt subtraction → Number conversion for milliseconds
        const nanos = end - begin
        return [name, convertNanoToMilliSeconds(Number(nanos))]
      }),
    ) as Record<T, number>
    timestampMappable.unmap()
    latest.push(results)
    if (latest.length > avgCount) {
      latest.shift()
    }
  }

  function average(): Readonly<Record<T, number>> | undefined {
    const count = latest.length
    if (count < avgCount) {
      return undefined
    }
    return Object.fromEntries(
      timestampNames.map((name) => [
        name,
        sum(latest.map((current) => current[name])) / count,
      ]),
    ) as Readonly<Record<T, number>>
  }

  return {
    timestampWrites,
    write,
    read,
    average,
  }
}
